import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { appInfo } from "@/platform";
import { ChevronLeft } from "lucide-react";
import Topbar from "../topbar/TopBar";
import Aside from "../aside/Aside";

// Add this custom hook at the top of the file
const useIsLargeScreen = () => {
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1024); // 1024px is the 'lg' breakpoint in Tailwind
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return isLargeScreen;
};

interface PageProps {
  children: React.ReactNode;
  isDemo: boolean;
  noSidebar?: boolean;
}

function Page({ children, isDemo, noSidebar }: PageProps) {
  const [showAside, setShowAside] = useState(false);
  const [version, setVersion] = useState<string>("");
  const isLargeScreen = useIsLargeScreen();
  const location = useLocation();

  // Hide Aside on /chat page to avoid conflicts with the main chat
  const isChatPage = location.pathname === "/chat";

  useEffect(() => {
    const getVersionApp = async () => {
      const version = await appInfo.getVersion();
      setVersion(version);
    };

    getVersionApp();
  }, []);

  const toggleAside = () => {
    setShowAside(!showAside);
  };

  return (
    <div
      className={`flex-1 ${
        isDemo
          ? "min-h-[calc(100vh-35px)] max-h-[calc(100vh-35px)]"
          : "min-h-screen max-h-screen"
      } sticky top-0 overflow-hidden flex flex-col gap-0 bg-background-100 transition-all duration-300 md:min-w-[280px]`}
    >
      <Topbar noSidebar={noSidebar} />

      <div
        className={`grid lg:grid-cols-[1fr_auto_auto] gap-2 pb-0 pl-2.5 ${
          !isLargeScreen || showAside ? "pr-2.5" : "pr-0"
        } bg-background-100 transition-all duration-300 overflow-hidden flex-1 grid-cols-1`}
      >
        {/* Main Content */}
        <div className="h-[calc(100vh-90px)] rounded-md bg-background-200 overflow-y-scroll overflow-x-hidden scrollbar-thin border border-border transition-all duration-300 text-text-primary">
          {children}
        </div>

        {/* Arrow Toggle Button - Hidden on /chat page */}
        {isLargeScreen && !noSidebar && !isChatPage && (
          <div
            className="w-auto h-full bg-background-200 rounded-[20px] transition-all duration-300 sticky top-0 flex justify-center items-center cursor-pointer"
            onClick={toggleAside}
          >
            <ChevronLeft
              className={`text-text-secondary self-center p-[5px] w-[30px] h-[30px] rounded-full flex justify-center items-center transition-all duration-300 cursor-pointer hover:bg-text-primary hover:text-background ${
                showAside ? "rotate-180" : "rotate-0"
              }`}
            />
          </div>
        )}

        {/* Aside Panel - Hidden on /chat page */}
        {isLargeScreen && !noSidebar && !isChatPage && (
          <div
            className={`${
              showAside ? "w-[400px] flex" : "w-0 hidden"
            } rounded-md bg-background-200 border border-border p-0 overflow-hidden overflow-y-auto transition-all duration-300`}
          >
            <div className="text-text-primary h-auto w-full">
              <Aside />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-center items-center text-text-primary bg-background-100 transition-all duration-300 text-center mx-auto h-8 max-h-[700px]:max-w-[600px]:hidden">
        <div className="text-xs text-text-secondary">
          {`${new Date().getFullYear()}`}{" "}
          <span className="font-semibold">Trud</span>®{" "}
          {`🧡 Todos los derechos reservados. / v${version}`}
        </div>
      </div>
    </div>
  );
}

export default Page;
